import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { DropResult } from '@hello-pangea/dnd';

import { useProjectFiltering } from '~/hooks/useProjectFiltering';
import { useProjectOperations } from '~/hooks/useProjectOperations';
import { useProjects } from '~/hooks/useProjects';
import { useTaskOperations } from '~/hooks/useTaskOperations';
import { useTasks } from '~/hooks/useTasks';
import { useUIStore } from '~/stores/uiStore';
import { logger } from '~/utils/logger';

import { ErrorBoundary } from '~/components/ErrorBoundary';

import type { ProjectStatus, TaskProps, TaskStatus } from '~/types';

import { ProjectAssistant } from './Assistant';
import { ProjectsBoard } from './Board';
import { ProjectView } from './ProjectView';
import { ProjectsSidebar } from './Sidebar';

export function Projects(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { editingProjectIds, editingTaskId, setSelectedTask } = useUIStore();

  // Check if we're on a nested route (like /review) that should render via Outlet
  const isNestedRoute = location.pathname.includes('/review');

  const { error: projectsError, isLoading: projectsLoading, projects = [] } = useProjects();

  const projectsWithEditing = useMemo(() => {
    return projects.map(project => ({
      ...project,
      prdContent: project.prdContent || undefined,
      description: project.description || '',
      isEditing: editingProjectIds.has(project.id),
    }));
  }, [projects, editingProjectIds]);

  // Filtering lives here so Projects can derive effectiveProjectId without an effect.
  // List receives the filtering result as a prop and renders it — no filtering logic
  // or reconciliation effects needed inside List.
  const filtering = useProjectFiltering(projectsWithEditing);

  // The project the board should display: only set when the URL project is actually
  // visible in the current filter context. When filters hide it the board stays blank
  // without any navigate() call — the URL is preserved so the user can adjust filters
  // and recover without losing context.
  const effectiveProjectId = useMemo(() => {
    if (projectsLoading || !projectId) return null;
    return filtering.filteredProjects.some(p => p.id === projectId) ? projectId : null;
  }, [projectsLoading, projectId, filtering.filteredProjects]);

  // Navigation callback for URL-based routing.
  // setSelectedTask(null) is called here (event-driven) instead of in a useEffect,
  // because we know the project is changing at the moment the user or an operation
  // triggers this.
  const handleProjectSelect = useCallback(
    (id: string | null) => {
      setSelectedTask(null);
      if (id) {
        window.localStorage.setItem('last-selected-project-id', id);
        if (id !== projectId) {
          void navigate(`/projects/${id}`);
        }
      } else {
        void navigate('/projects');
      }
    },
    [navigate, projectId, setSelectedTask],
  );

  const { handleGenerateTasksFromPRD, handleProjectDelete, handleProjectUpdate } =
    useProjectOperations(handleProjectSelect);

  const {
    handleSubtaskCreate,
    handleSubtaskDelete,
    handleSubtaskUpdate,
    handleTaskCreate,
    handleTaskDelete,
    handleTaskSelect,
    handleTaskUpdate,
  } = useTaskOperations(effectiveProjectId);

  // Fetch tasks only for the effective (visible) project — avoids loading data for a
  // project that is hidden by the current filter context.
  const { error: tasksError, isLoading: tasksLoading, tasks = [] } = useTasks(effectiveProjectId);

  // Validate that the URL projectId belongs to this user's project list, and
  // auto-heal by redirecting to the first project if not. Only applies to
  // the legacy sidebar+board layout (the /review nested route here — see
  // the ProjectView early return above) — for a non-review projectId,
  // ProjectView now owns that URL and renders its own not-found state for
  // an invalid ID; redirecting away from under it here would silently swap
  // in an unrelated project instead of surfacing the error.
  // Runs only after data arrives to avoid wrongly redirecting during the initial fetch.
  useEffect(() => {
    if (!projectsLoading && projectId && isNestedRoute && projects.length > 0) {
      const projectExists = projects.some(p => p.id === projectId);
      if (!projectExists) {
        void navigate(`/projects/${projects[0].id}`, { replace: true });
      }
    }
  }, [projectsLoading, projects, projectId, isNestedRoute, navigate]);

  // Restore the last-selected (or first visible) project when navigating to /projects
  // with no projectId. Uses filteredProjects so the restored project is always in the
  // current filter context.
  useEffect(() => {
    if (!projectsLoading && !projectId && filtering.filteredProjects.length > 0) {
      const lastId = localStorage.getItem('last-selected-project-id');
      const lastProject = lastId ? filtering.filteredProjects.find(p => p.id === lastId) : null;
      void navigate(`/projects/${(lastProject ?? filtering.filteredProjects[0]).id}`, {
        replace: true,
      });
    }
  }, [projectsLoading, filtering.filteredProjects, projectId, navigate]);

  const tasksWithUI = useMemo(() => {
    return tasks.map(
      (task): TaskProps => ({
        ...task,
        isEditing: task.id === editingTaskId,
        subtasks: task.subtasks.map(subtask => ({
          ...subtask,
          isEditing: false,
        })),
      }),
    );
  }, [tasks, editingTaskId]);

  const currentProject = useMemo(() => {
    return effectiveProjectId ? projects.find(p => p.id === effectiveProjectId) : undefined;
  }, [projects, effectiveProjectId]);

  const handleProjectStatusChange = useCallback(
    (projectId: string, status: string) => {
      const project = projects.find(p => p.id === projectId);
      if (!project) {
        return;
      }
      handleProjectUpdate(projectId, { status: status as ProjectStatus });
    },
    [projects, handleProjectUpdate],
  );

  const handleStatusChange = useCallback(
    (taskId: string, status: TaskStatus) => {
      void handleTaskUpdate(taskId, { status });
    },
    [handleTaskUpdate],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const taskId = result.draggableId;
      const newStatus = result.destination.droppableId as TaskStatus;
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        return;
      }

      logger.debug({ component: 'App' }, 'Drag ended', {
        taskId,
        currentStatus: task.status,
        newStatus,
        task,
      });

      void handleTaskUpdate(taskId, { status: newStatus });
    },
    [tasks, handleTaskUpdate],
  );

  // Standalone drill route: a selected, non-review project renders ProjectView's
  // full-page Kanban board instead of the legacy sidebar + ProjectsBoard combo.
  // Placed after all hooks above (never before them) so hook call order/count
  // stays identical across renders regardless of projectId/isNestedRoute — only
  // the returned JSX branches. Mirrors ProductsLayout's productId-based split
  // (src/pages/Products/ProductsLayout.tsx), except mounted here in Projects.tsx
  // rather than in App.tsx/a router split, per this task's scope.
  if (projectId && !isNestedRoute) {
    return <ProjectView projectId={projectId} />;
  }

  return (
    <div className="relative h-full bg-background text-foreground">
      {/* Main content area - full width */}
      <div className="flex h-full overflow-hidden">
        <ErrorBoundary>
          <ProjectsSidebar
            projects={projectsWithEditing}
            filtering={filtering}
            selectedProjectId={projectId ?? null}
            onProjectSelect={handleProjectSelect}
            onProjectStatusChange={handleProjectStatusChange}
            onProjectUpdate={handleProjectUpdate}
            onProjectDelete={handleProjectDelete}
            onGenerateTasksFromPRD={handleGenerateTasksFromPRD}
            isLoading={projectsLoading}
            error={projectsError instanceof Error ? projectsError.message : null}
          />
        </ErrorBoundary>

        <ErrorBoundary>
          {isNestedRoute ? (
            <div className="flex-1 min-w-0 h-full">
              <Outlet />
            </div>
          ) : (
            <ProjectsBoard
              tasks={effectiveProjectId ? tasksWithUI : null}
              isLoading={tasksLoading}
              error={tasksError}
              currentProject={currentProject}
              onTaskCreate={handleTaskCreate}
              onTaskSelect={handleTaskSelect}
              onTaskUpdate={(taskId, updates) => void handleTaskUpdate(taskId, updates)}
              onTaskDelete={handleTaskDelete}
              onSubtaskCreate={handleSubtaskCreate}
              onSubtaskUpdate={handleSubtaskUpdate}
              onSubtaskDelete={handleSubtaskDelete}
              onStatusChange={handleStatusChange}
              onDragEnd={handleDragEnd}
            />
          )}
        </ErrorBoundary>
      </div>

      {/* Assistant panel - overlay on top */}
      <ErrorBoundary>
        <ProjectAssistant
          projectId={projectId || undefined}
          projectName={currentProject?.title}
          projectDetails={currentProject?.details}
          existingPRD={currentProject?.prdContent}
          onSavePRD={
            projectId
              ? (prdContent: string): void => {
                  handleProjectUpdate(projectId, { prdContent });
                }
              : undefined
          }
        />
      </ErrorBoundary>
    </div>
  );
}
